<template>
  <div>
    <v-dialog
      v-model="helpDialog"
      max-width="600"
      scrollable
    >
      <template #activator="{ on, attrs }">
        <v-btn
          color="primary"
          dark
          v-bind="attrs"
          v-on="on"
          :outlined="outlined"
          :text="!outlined"
          small
        >
          <v-icon left>
            mdi-help-circle-outline
          </v-icon>
          {{ $t('live.studentHelpDialog.button') }}
        </v-btn>
      </template>

      <eva-step-dialog
        :title="currentTitle"
        :back-button="currentView !== 0"
        @back="currentView = 0"
        @close="helpDialog = false"
        @dblclick:title="currentView = 4"
      >
        <template #content>
          <v-window
            v-model="currentView"
            class="px-2"
          >
            <v-window-item :value="0">
              <v-card-subtitle>
                {{ $t('live.studentHelpDialog.subtitle') }}
              </v-card-subtitle>

              <v-card-text>
                <div>
                  <v-card
                    v-for="(option, index) in helpOptions"
                    :key="index"
                    @click="option.action"
                    outlined
                    flat
                    class="mb-4"
                  >
                    <v-list-item>
                      <v-list-item-icon class="mr-4">
                        <eva-big-icon
                          :icon="option.icon"
                          :size="30"
                          color="blue"
                        />
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ $t(option.title) }}
                        </v-list-item-title>
                        <v-list-item-subtitle>{{ $t(option.description) }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-card>
                </div>
              </v-card-text>
            </v-window-item>

            <v-window-item :value="1">
              <v-card-text>
                <quiz-student-info-summary
                  :quiz="quiz"
                  :quizzes-attempt-summary="quizzesAttemptSummary"
                  :session="session"
                />
              </v-card-text>
            </v-window-item>

            <v-window-item :value="2">
              <v-card-text class="text-subtitle-1 mx-4">
                {{ $t('live.studentHelpDialog.urgentTechnicalIssue.steps.chat.description') }}
              </v-card-text>
            </v-window-item>

            <v-window-item :value="3">
              <v-card-text class="text-subtitle-1 px-8">
                <katex-md :expr="$t('live.studentHelpDialog.urgentTechnicalIssue.steps.email.instructions.email')" />

                <v-divider class="my-4" />
                <katex-md
                  :expr="$t('live.studentHelpDialog.urgentTechnicalIssue.steps.email.instructions.organizer')"
                />
              </v-card-text>
            </v-window-item>

            <v-window-item :value="4">
              <v-card
                outlined
                flat
                class="mx-4"
              >
                <exam-diagnostic-info
                  :quiz-id="quiz.id"
                  :quizzes-attempt-summary-id="quizzesAttemptSummary.id"
                />
              </v-card>
            </v-window-item>
          </v-window>
        </template>
        <template #actions>
          <template v-if="currentView === 1">
            <eva-button
              block
              primary
              @click="handleContactOrganizer"
              :label="$t('live.studentHelpDialog.examRules.step.button')"
              class="mx-2 mb-2"
            />
          </template>
          <template v-else-if="currentView === 2">
            <eva-button
              block
              primary
              @click="handleContactSupport"
              :label="$t('live.studentHelpDialog.urgentTechnicalIssue.steps.chat.button')"
              class="mx-2 mb-2"
            />
          </template>
        </template>
      </eva-step-dialog>
    </v-dialog>
  </div>
</template>

<script>
import chatSupportHelpers from "../../../helpers/chat_support_helpers"
import ExamDiagnosticInfo from "./exam_diagnostic_info.vue"
import EvaBigIcon from "@/components/shared/eva-big-icon.vue"
import EvaStepDialog from "@/components/shared/eva-step-dialog.vue"
import KatexMd from "@/components/katex-md.vue"
import QuizStudentInfoSummary from "@/components/quiz/evalmee/quiz_student_info_summary.vue"

export default {
  name: "StudentHelpMenu",
  components: {
    ExamDiagnosticInfo,
    EvaBigIcon,
    EvaStepDialog,
    KatexMd,
    QuizStudentInfoSummary,
  },
  props: {
    outlined: {
      type: Boolean,
      default: true,
    },
    quiz: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    quizzesAttemptSummary: {
      type: Object,
      default: () => ({}),
      required: true,
    },
  },
  data() {
    return {
      diagnosticDialog: false,
      helpDialog: false,
      currentView: 0,
      clickTimes: [],
      helpOptions: [
        {
          title: "live.studentHelpDialog.examRules.title",
          description: "live.studentHelpDialog.examRules.description",
          icon: "mdi-book-open-variant-outline",
          action: () => this.currentView = 1,
        },
        {
          title: "live.studentHelpDialog.examSubject.title",
          description: "live.studentHelpDialog.examSubject.description",
          icon: "mdi-order-bool-ascending-variant",
          action: this.handleExamSubject,
        },
        {
          title: "live.studentHelpDialog.misconductReport.title",
          description: "live.studentHelpDialog.misconductReport.description",
          icon: "mdi-account-alert-outline",
          action: this.handleMisconductReport,
        },
        {
          title: "live.studentHelpDialog.technicalIssue.title",
          description: "live.studentHelpDialog.technicalIssue.description",
          icon: "mdi-alert-circle-outline",
          action: this.handleTechnicalIssue,
        },
        {
          title: "live.studentHelpDialog.urgentTechnicalIssue.title",
          description: "live.studentHelpDialog.urgentTechnicalIssue.description",
          icon: "mdi-car-emergency",
          action: this.handleUrgentTechnicalIssue,
        },
      ],
    }
  },
  computed: {
    currentTitle() {
      switch (this.currentView) {
      case 0:
        return this.$t("live.studentHelpDialog.title")
      case 1:
        return this.$t("live.studentHelpDialog.examRules.step.title")
      case 2:
        return this.$t("live.studentHelpDialog.urgentTechnicalIssue.steps.chat.title")
      case 3:
        return this.$t("live.studentHelpDialog.urgentTechnicalIssue.steps.email.title")
      default:
        return this.$t("live.studentHelpDialog.title")
      }
    },
  },
  methods: {
    showCrispChat() {
      chatSupportHelpers.showCrispChat()
    },
    showChat() {
      this.$root.$emit("display-chat")
    },
    handleExamRules() {
      this.currentView = 1
    },
    handleExamSubject() {
      this.showChat()
      this.$root.$emit("prefill-chat", this.$t("live.studentHelpDialog.examSubject.chatMessageTemplate"))
      this.helpDialog = false
    },
    handleMisconductReport() {
      this.showChat()
      this.$root.$emit("prefill-chat", this.$t("live.studentHelpDialog.misconductReport.chatMessageTemplate"))
      this.helpDialog = false
    },
    handleTechnicalIssue() {
      this.showCrispChat()
      chatSupportHelpers.prefillCrispMessage(this.$t("live.studentHelpDialog.technicalIssue.chatMessageTemplate"))
      this.helpDialog = false
    },
    handleUrgentTechnicalIssue() {
      this.showCrispChat()
      chatSupportHelpers.prefillCrispMessage(this.$t("live.studentHelpDialog.urgentTechnicalIssue.chatMessageTemplate"))
      this.currentView = 2
    },
    handleContactSupport() {
      this.currentView = 3
    },
    handleContactOrganizer() {
      this.showChat()
      this.helpDialog = false
    },
  },
  watch: {
    helpDialog(newValue) {
      if (newValue) {
        this.currentView = 0
      }
    },
  },
}
</script>
